import { useContext, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { LanguageContext } from "@/i18n";
import { BLOG_POSTS, BLOG_UI } from "@/blog/posts";
import Seo from "@/components/Seo";

const EASE = [0.16, 1, 0.3, 1];

export default function BlogList() {
  const { lang, t } = useContext(LanguageContext);
  const navigate = useNavigate();
  const ui = BLOG_UI[lang] || BLOG_UI.en;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="blog-list" className="pt-36 pb-28 min-h-screen">
      <Seo lang={lang} t={t} title={`${ui.listTitle} | DADABIO`} desc={ui.listSub} path={`/${lang}/blog`} />
      <div className="max-w-screen-xl mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#00F0FF] mb-6"
        >
          {ui.menu}
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.08, ease: EASE }}
          className="font-display font-black tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.02] mb-6"
        >
          {ui.listTitle}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-base md:text-lg font-light text-zinc-400 max-w-xl mb-16"
        >
          {ui.listSub}
        </motion.p>

        <div className="grid md:grid-cols-2 gap-6">
          {BLOG_POSTS.map((post, i) => {
            const c = post.langs[lang] || post.langs.en;
            return (
              <motion.button
                key={post.slug}
                data-testid={`blog-card-${post.slug}`}
                initial={{ opacity: 0, y: 36 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.25 + i * 0.12, ease: EASE }}
                onClick={() => navigate(`/${lang}/blog/${post.slug}`)}
                className="group text-start rounded-2xl border border-white/10 bg-[#121212] overflow-hidden hover:border-[#00F0FF]/40 hover:shadow-[0_0_50px_rgba(0,240,255,0.07)] transition-[border-color,box-shadow] duration-500"
              >
                <div className="overflow-hidden">
                  <img
                    src={post.img}
                    alt={c.title}
                    loading="lazy"
                    className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  />
                </div>
                <div className="p-7">
                  <p className="font-mono text-[11px] tracking-[0.2em] text-zinc-500 mb-3">{post.date}</p>
                  <h2 className="font-display font-bold text-xl md:text-2xl text-white leading-snug mb-3 group-hover:text-[#00F0FF] transition-colors duration-300">
                    {c.title}
                  </h2>
                  <p className="text-sm font-light text-zinc-400 leading-relaxed mb-5">{c.excerpt}</p>
                  <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#00F0FF]">
                    {ui.readMore}
                    <ArrowUpRight size={14} className="rtl:-scale-x-100" />
                  </span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </main>
  );
}
